import { RequirePermission } from "@components/common/require-permission";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { LinkWithQRCodeDialog } from "@components/common/link-with-qrcode-dialog";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { ActionWithHelp } from "@components/common/action-with-help";
import { BaseMemberIdProps } from "@custom-types/base-types";
import { MemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useState } from "react";
import { Button, NotificationDialog, useToast, Text } from "@flat-ui";

interface Props extends BaseMemberIdProps {
  /** The current authentication provider id for the member. */
  providerId: FaroCoreApiTypes.UserProviderId;
}

/** Adds a button to reset the member's password if their authentication provider is email. */
export function ResetMemberPassword({
  memberId,
  providerId,
}: Props): React.JSX.Element {
  const { handleErrorWithToast } = useErrorHandler();
  const { trackEvent } = useTrackEvent();
  const [shouldShowConfirmationDialog, setShouldShowConfirmationDialog] =
    useState<boolean>(false);
  const [newPWLink, setNewPWLink] = useState<string>("");
  const { openToast } = useToast();

  async function onResetPasswordRequest(): Promise<void> {
    trackEvent({ name: MemberEvents.generateResetPasswordLink });

    try {
      const { newPWLink } = await faroCoreApiClient.admin.generateResetPWLink({
        memberId,
      });

      setNewPWLink(newPWLink);

      openToast({
        title:
          "A password reset email has been sent to the member's email address.",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `reset-password-${Date.now().toString()}`,
        title: "Could not reset member password",
        error,
      });
    } finally {
      setShouldShowConfirmationDialog(false);
    }
  }

  return (
    <>
      <ActionWithHelp
        helpTitle={
          providerId === "email"
            ? "Sends a password reset link to the member's email address."
            : "Password reset is not available for members using third-party authentication providers."
        }
      >
        <RequirePermission permission="member.resetPassword">
          <Button
            disabled={providerId !== "email"}
            onClick={() => {
              trackEvent({
                name: MemberEvents.openResetPasswordLinkDialog,
              });
              setShouldShowConfirmationDialog(true);
            }}
            variant="secondary"
          >
            Reset Password
          </Button>
        </RequirePermission>
      </ActionWithHelp>

      <NotificationDialog
        open={shouldShowConfirmationDialog}
        notificationType="warning"
        title="Sending password reset link"
        onCancel={() => setShouldShowConfirmationDialog(false)}
        onConfirm={onResetPasswordRequest}
      >
        <Text variant="bodyL">
          Are you sure you want to send a password reset email to the member?
          This will:
          <ul>
            <li>
              Create a new password reset link and send it to the member's email
            </li>
            <li>
              Invalidate any existing password reset links for this member
            </li>
          </ul>
        </Text>
      </NotificationDialog>

      {newPWLink && (
        <LinkWithQRCodeDialog
          title="New password reset link"
          onDialogClose={() => setNewPWLink("")}
          link={newPWLink}
          location="new password reset link"
        />
      )}
    </>
  );
}
